<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { LocaleCode, LocaleOption } from './catalog'

const props = withDefaults(
  defineProps<{
    locale: LocaleCode
    options: LocaleOption[]
    label?: string
    /** dropdown = globe popover; list = always-visible stack (mobile drawer) */
    variant?: 'dropdown' | 'list'
  }>(),
  { variant: 'dropdown' },
)

const emit = defineEmits<{
  change: [code: LocaleCode]
}>()

const open = ref(false)
const root = ref<HTMLElement | null>(null)
const menu = ref<HTMLElement | null>(null)

const isList = computed(() => props.variant === 'list')

function toggle() {
  if (isList.value) return
  open.value = !open.value
}

function select(code: LocaleCode) {
  emit('change', code)
  open.value = false
}

function onDocPointer(ev: Event) {
  if (isList.value) return
  const target = ev.target
  if (!(target instanceof Node)) return
  const inRoot = root.value?.contains(target)
  const inMenu = menu.value?.contains(target)
  if (!inRoot && !inMenu) open.value = false
}

function onKey(ev: KeyboardEvent) {
  if (ev.key === 'Escape') open.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointer)
  document.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointer)
  document.removeEventListener('keydown', onKey)
})
</script>

<template>
  <div
    ref="root"
    class="locale-picker"
    :class="{ 'is-list': isList }"
    data-testid="locale-picker"
  >
    <template v-if="isList">
      <div class="list-label">{{ label || 'Language' }}</div>
      <div class="list" role="listbox" :aria-label="label || 'Language'">
        <button
          v-for="opt in options"
          :key="opt.code"
          type="button"
          role="option"
          :data-testid="opt.testId"
          :aria-selected="opt.code === locale"
          :class="{ active: opt.code === locale }"
          @click="select(opt.code)"
        >
          <span class="opt-label">{{ opt.label }}</span>
        </button>
      </div>
    </template>

    <template v-else>
      <button
        type="button"
        class="globe-btn"
        :aria-label="label || 'Language'"
        :aria-expanded="open"
        aria-haspopup="menu"
        data-testid="locale-globe"
        @click="toggle"
      >
        <svg class="globe" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6" />
          <path
            d="M3.5 12h17M12 3c2.5 2.8 3.8 5.8 3.8 9s-1.3 6.2-3.8 9c-2.5-2.8-3.8-5.8-3.8-9s1.3-6.2 3.8-9z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
          />
        </svg>
        <span class="chev" :class="{ open }" aria-hidden="true" />
      </button>

      <Teleport to="body">
        <div
          v-if="open"
          ref="menu"
          class="locale-picker-menu"
          role="menu"
          :aria-label="label || 'Language'"
        >
          <button
            v-for="opt in options"
            :key="opt.code"
            type="button"
            role="menuitemradio"
            :data-testid="opt.testId"
            :aria-checked="opt.code === locale"
            :class="{ active: opt.code === locale }"
            @click="select(opt.code)"
          >
            <span class="opt-label">{{ opt.label }}</span>
          </button>
        </div>
      </Teleport>
    </template>
  </div>
</template>

<style scoped>
.locale-picker {
  position: relative;
  display: inline-flex;
  align-items: center;
  overflow: visible;
}

.locale-picker.is-list {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 8px;
}

.list-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #888888;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.list button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.65rem;
  border: 1px solid #2a2a2a;
  background: #0a0a0a;
  color: #cccccc;
  text-align: left;
  border-radius: 10px;
  padding: 12px 14px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.list button:hover,
.list button:focus-visible {
  border-color: #ff3333;
  color: #ffffff;
  outline: none;
}

.list button.active {
  border-color: #ff3333;
  background: rgba(255, 51, 51, 0.14);
  color: #ff3333;
}

.globe-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid color-mix(in srgb, currentColor 22%, transparent);
  background: color-mix(in srgb, currentColor 6%, transparent);
  color: inherit;
  border-radius: 0.45rem;
  padding: 0.35rem 0.55rem;
  font: inherit;
  font-size: 0.8rem;
  cursor: pointer;
  line-height: 1;
}

.globe-btn:hover,
.globe-btn:focus-visible {
  background: color-mix(in srgb, currentColor 12%, transparent);
  outline: none;
}

.globe {
  width: 1.15rem;
  height: 1.15rem;
  flex-shrink: 0;
}

.chev {
  width: 0;
  height: 0;
  border-left: 0.28rem solid transparent;
  border-right: 0.28rem solid transparent;
  border-top: 0.32rem solid currentColor;
  opacity: 0.65;
  transition: transform 0.15s ease;
}

.chev.open {
  transform: rotate(180deg);
}

.opt-label {
  flex: 1;
}
</style>

<!-- Teleported menu: must not live under backdrop-filter ancestors (theme chrome). -->
<style>
.locale-picker-menu {
  position: fixed;
  top: 56px;
  right: 12px;
  z-index: 10070;
  min-width: 13.5rem;
  max-width: min(90vw, 20rem);
  max-height: min(70vh, 24rem);
  overflow: auto;
  padding: 0.35rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: #141820;
  color: #f2f4f7;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);
  box-sizing: border-box;
}

.locale-picker-menu button {
  display: flex;
  width: 100%;
  align-items: center;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  border-radius: 0.35rem;
  padding: 0.65rem 0.75rem;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.locale-picker-menu button:hover,
.locale-picker-menu button:focus-visible {
  background: rgba(255, 255, 255, 0.08);
  outline: none;
}

.locale-picker-menu button.active {
  background: rgba(225, 29, 46, 0.88);
}

@media (max-width: 900px) {
  .locale-picker-menu {
    left: 12px;
    right: 12px;
    top: auto;
    bottom: 12px;
    min-width: 0;
    max-width: none;
    max-height: min(55vh, 22rem);
    border-radius: 14px;
    padding: 0.5rem;
    box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.55);
  }

  .locale-picker-menu button {
    padding: 0.85rem 0.85rem;
    font-size: 1rem;
  }
}
</style>
